"use client";

import { useEffect, useState, useRef } from "react";
import { isWindowDefined } from "@huub-web/utils";

type UseParallaxOptions = {
  /**
   * Parallax intensity factor (0-1)
   * 0.2 means the element moves 20% of the scroll distance
   */
  intensity?: number;
  /**
   * Whether to enable the parallax effect
   */
  enabled?: boolean;
  /**
   * Whether to invert the parallax direction
   * When true, elements move in the opposite direction
   */
  inverted?: boolean;
};

/**
 * Hook that creates a parallax scrolling effect for an element
 * Makes the element move at a different speed than the scroll
 *
 * @param options.intensity - Parallax intensity factor (0-1)
 * @param options.enabled - Whether to enable the parallax effect
 * @param options.inverted - Whether to invert the parallax direction
 */
export const useParallax = <T extends HTMLElement = HTMLElement>(
  options: UseParallaxOptions = {},
) => {
  const { intensity = 0.2, enabled = true, inverted = false } = options;
  const [transform, setTransform] = useState(
    `translateY(0px) scale(${intensity * 3 + 1})`,
  );
  const elementRef = useRef<T | null>(null);

  const ref = (node: T | null) => {
    elementRef.current = node;
  };

  useEffect(() => {
    if (!enabled || !isWindowDefined()) {
      return;
    }

    const handleScroll = () => {
      if (!elementRef.current) {
        return;
      }

      const rect = elementRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Calculate how much of the element is visible
      // Positive when element is above viewport center, negative when below
      const elementCenter = rect.top + rect.height / 2;
      const viewportCenter = windowHeight / 2;
      const distanceFromCenter = elementCenter - viewportCenter;

      // Calculate parallax offset based on distance from viewport center
      const parallaxOffset =
        distanceFromCenter * intensity * (inverted ? -1 : 1);

      setTransform(
        `translateY(${parallaxOffset}px) scale(${intensity * 4 + 1})`,
      );
    };

    // Initial calculation
    handleScroll();

    // Add scroll listener with throttling for performance
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", throttledScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", throttledScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [intensity, enabled, inverted]);

  return {
    ref,
    style: {
      transform: enabled ? transform : undefined,
      transition: enabled ? undefined : "transform 0.3s ease-out",
    },
  };
};
